(() => {
  const instances = new Map();

  function setButtonState(button, isPlaying) {
    button.classList.toggle('is-playing', isPlaying);
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');

    const srText = button.querySelector('.rf-video-carousel__toggle-text');
    if (srText) {
      srText.textContent = isPlaying ? 'Pause video' : 'Play video';
    }
  }

  function safeVideoSource(videoSetting) {
    if (!videoSetting) return null;
    if (videoSetting.currentSrc) return videoSetting.currentSrc;
    if (videoSetting.src) return videoSetting.src;
    return null;
  }

  function init(sectionElement) {
    if (!sectionElement || instances.has(sectionElement.id)) {
      return;
    }

    const carousel = sectionElement.querySelector('[data-rf-video-carousel]');
    if (!carousel) {
      return;
    }

    const sectionId = carousel.dataset.sectionId;
    const sliderSelector = `#rf-video-swiper-${sectionId}`;
    const sliderElement = sectionElement.querySelector(sliderSelector);

    if (!sliderElement) {
      return;
    }

    let readyTimeout = null;
    const eventUnbinds = [];
    let swiperInstance = null;

    const setup = () => {
      if (typeof window.Swiper === 'undefined') {
        readyTimeout = window.setTimeout(setup, 100);
        return;
      }

      swiperInstance = new window.Swiper(sliderSelector, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        grabCursor: true,
        centeredSlides: false,
        watchOverflow: true,
        breakpoints: {
          750: {
            slidesPerView: 'auto',
            spaceBetween: 24
          },
          990: {
            slidesPerView: 'auto',
            spaceBetween: 48
          }
        },
        navigation: {
          nextEl: `#shopify-section-${sectionId} .rf-video-carousel__nav-next`,
          prevEl: `#shopify-section-${sectionId} .rf-video-carousel__nav-prev`
        },
        pagination: {
          el: `#rf-video-pagination-${sectionId}`,
          clickable: true,
          dynamicBullets: false
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous video',
          nextSlideMessage: 'Next video',
          firstSlideMessage: 'This is the first video',
          lastSlideMessage: 'This is the last video'
        },
        on: {
          init() {
            const videos = sliderElement.querySelectorAll('video');
            videos.forEach((video) => {
              video.pause();
              video.currentTime = 0;
              video.muted = true;
            });
          },
          slideChange() {
            const videos = sliderElement.querySelectorAll('video');
            videos.forEach((video) => {
              video.pause();
              video.currentTime = 0;
              video.muted = true;

              const button = video.closest('.rf-video-carousel__media')?.querySelector('[data-rf-video-toggle]');
              if (button) {
                setButtonState(button, false);
              }
            });
          }
        }
      });

      const allVideos = Array.from(sliderElement.querySelectorAll('video'));
      const allButtons = Array.from(sliderElement.querySelectorAll('[data-rf-video-toggle]'));

      function pauseAllExcept(activeVideo) {
        allVideos.forEach((video) => {
          const button = video.closest('.rf-video-carousel__media')?.querySelector('[data-rf-video-toggle]');
          if (video !== activeVideo) {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
            if (button) {
              setButtonState(button, false);
            }
          }
        });
      }

      allButtons.forEach((button) => {
        const onButtonClick = (event) => {
          event.preventDefault();
          event.stopPropagation();

          const media = button.closest('.rf-video-carousel__media');
          const video = media ? media.querySelector('video') : null;
          if (!video) {
            return;
          }

          if (video.paused) {
            pauseAllExcept(video);
            video.muted = false;
            video.play();
            setButtonState(button, true);
          } else {
            video.pause();
            video.muted = true;
            setButtonState(button, false);
          }
        };

        button.addEventListener('click', onButtonClick);
        eventUnbinds.push(() => button.removeEventListener('click', onButtonClick));
      });

      allVideos.forEach((video) => {
        const onVideoClick = (event) => {
          if (video.paused) {
            return;
          }

          event.preventDefault();
          video.pause();
          video.muted = true;

          const button = video.closest('.rf-video-carousel__media')?.querySelector('[data-rf-video-toggle]');
          if (button) {
            setButtonState(button, false);
          }
        };

        const onEnded = () => {
          video.muted = true;
          const button = video.closest('.rf-video-carousel__media')?.querySelector('[data-rf-video-toggle]');
          if (button) {
            setButtonState(button, false);
          }
        };

        video.addEventListener('click', onVideoClick);
        video.addEventListener('ended', onEnded);

        eventUnbinds.push(() => video.removeEventListener('click', onVideoClick));
        eventUnbinds.push(() => video.removeEventListener('ended', onEnded));
      });
    };

    setup();

    instances.set(sectionElement.id, {
      destroy() {
        if (readyTimeout) {
          window.clearTimeout(readyTimeout);
        }

        eventUnbinds.forEach((unbind) => unbind());

        if (swiperInstance && typeof swiperInstance.destroy === 'function') {
          swiperInstance.destroy(true, true);
        }
      }
    });
  }

  function destroy(sectionId) {
    const instance = instances.get(sectionId);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initAll() {
    document.querySelectorAll('[data-rf-video-carousel-root]').forEach((sectionElement) => {
      init(sectionElement);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionElement = event.target;
    if (sectionElement && sectionElement.matches('[data-rf-video-carousel-root]')) {
      init(sectionElement);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionElement = event.target;
    if (sectionElement && sectionElement.id) {
      destroy(sectionElement.id);
    }
  });
})();
